/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* istanbul ignore file */
import {
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  Menu,
  MenuButton,
  MenuList,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Stack,
  Switch,
  Text,
  Tooltip,
} from '@chakra-ui/core';
import { ChevronDownIcon } from '@chakra-ui/icons';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _some from 'lodash/some';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { MdOutlineInfo } from 'react-icons/md';
import useLanguagePhrasesByAccountId from '../../../../app/hooks/useLanguagePhrasesByAccountId';
import iconFood from '../../../../assets/img/icon-food.svg';
import iconHospitality from '../../../../assets/img/icon-hospitality.svg';
import iconManufacturing from '../../../../assets/img/icon-manufacturing.svg';
import iconRetail from '../../../../assets/img/icon-retail.svg';
import iconServices from '../../../../assets/img/icon-services.svg';
import iconTransportation from '../../../../assets/img/icon-transportation.svg';
import { DialogFlowAgent } from '../../../../firebase/firestore/documents/accounts';
import { LanguageEvaluationRating } from '../../../../firebase/firestore/documents/candidate';
import { LanguageEvaluationPhrases } from '../../../../firebase/firestore/documents/languageEvaluationPhrases';
import { Lookups } from '../../../../firebase/firestore/documents/lookups';
import { RemoteConfigKey } from '../../../../firebase/remoteConfig';
import { useStoreState } from '../../../../models/hooks';
import colors from '../../../../styles/colors';
import { clearErrorMessage, FormattedError, getErrorMessage, hasErrorMessage } from '../../../../utils/FormErrorUtils';
import { convertLegacyTextToHtml } from '../../../../utils/convertLegacyText';
import { getTranslatedStringValue } from '../../../../utils/localizedNumber';
import { getTranslatedValue } from '../../../../utils/localizedString';
import { MultiSelectDropdown } from '../../../common/MultiSelectDropDown/MultiSelectDropDown';
import { BusinessSelectType, IndustryType, JobPosition, JobType, OccupationType } from '../PositionType';
import useBusinessLocations from '../useBusinessLocations';
import { IndustryRadiosStyle, ReactCustomSelectStyles } from './CreateJobPositionStyle';
import DialogFlowSelect from './DialogFlowSelect';
import FitbandEvaluationSelect from './FitbandEvaluationSelect';
import HireFormTemplateSelect from './HireFormTemplateSelect';
import LanguageEvaluationTemplateSelect from './LanguageEvaluationTemplateSelect';
import ModernHireSelect from './ModernHireSelect';
import { AdditionalNoteEditor } from '../../../../app/components/AdditionalNoteEditor';
import { LanguageMap } from '../../../../firebase/firestore/documents/user';
import { useDocumentSnapshot } from '../../../../firebase/firestore/hooks';
import { Business } from '../BusinessType';
import { Collections } from '../../../../firebase/firestore/collections';
import { PositionType } from '../../../../firebase/firestore/documents/position';

export enum IconIndustry {
  TRANSPORTATION = 'Transportation',
  RETAIL = 'Retail',
  FOOD_AND_BEVERAGE = 'Food & Beverage Services',
  HOSPITALITY = 'Hospitality',
  SERVICES = 'Services',
  MANUFACTURING = 'Manufacturing',
}

export enum JobTypes {
  FULL_TIME = 'full_time',
  PART_TIME = 'part_time',
}

export enum SalaryDisplayOption {
  STARTING_AT = 'starting_at',
  RANGE = 'range',
  UP_TO = 'up_to',
  HIDDEN = 'hidden',
  FIXED = 'fixed',
}
export type RangeRequiredDisplayOptionType = SalaryDisplayOption.RANGE | SalaryDisplayOption.UP_TO;

export const salaryOptions = Object.values(SalaryDisplayOption);
export const rangeRequiredDisplayOptions: RangeRequiredDisplayOptionType[] = [
  SalaryDisplayOption.RANGE,
  SalaryDisplayOption.UP_TO,
];

const loadIcon = (industryType: string) => {
  switch (industryType) {
    case IconIndustry.TRANSPORTATION:
      return iconTransportation;
    case IconIndustry.FOOD_AND_BEVERAGE:
      return iconFood;
    case IconIndustry.HOSPITALITY:
      return iconHospitality;
    case IconIndustry.RETAIL:
      return iconRetail;
    case IconIndustry.SERVICES:
      return iconServices;
    case IconIndustry.MANUFACTURING:
      return iconManufacturing;
    default:
      return iconServices;
  }
};

export type JobPositionDetailProps = {
  jobPositionData: JobPosition;
  industries: IndustryType[];
  occupationList: OccupationType[];
  jobList: JobType[];
  formErrors: FormattedError[];
  setFormErrors: (errors: FormattedError[]) => void;
  salaryErrors: FormattedError[];
  audioEvaluationOptions?: Lookups[];
  positionDataToEdit: boolean;
  handleJobChange: (value: string, fieldName: string) => void;
  handleChange: (value: string | Date | number | boolean | null, fieldName: string, dataType: boolean) => void;
  handleSalaryChange: (value: string, fieldName: string) => void;
  handleLanguageEvaluationPhrase: (checked: boolean, language: string, phrases: LanguageEvaluationPhrases) => void;
  handleLanguageEvaluation: (checked: boolean) => void;
  handleDialogFlowCheckbox?: (checked: boolean) => void;
  handleLanguageChange: () => void;
  handleDialogFlowAgents?: (agent: DialogFlowAgent) => void;
  isSmbRecruiter?: boolean;
  setJobPositionData?: React.Dispatch<React.SetStateAction<JobPosition>>;
  isCopyPostion?: boolean;
  LETemplateUsed: boolean;
  setLETemplateUsed: React.Dispatch<React.SetStateAction<boolean>>;
};

export const JobPositionDetailCard = ({
  jobPositionData,
  industries,
  occupationList,
  jobList,
  formErrors,
  setFormErrors,
  positionDataToEdit,
  handleJobChange,
  handleChange,
  handleSalaryChange,
  handleLanguageEvaluationPhrase,
  handleLanguageEvaluation,
  handleDialogFlowCheckbox,
  handleLanguageChange,
  handleDialogFlowAgents,
  audioEvaluationOptions,
  isSmbRecruiter,
  setJobPositionData,
  isCopyPostion,
  LETemplateUsed,
  setLETemplateUsed,
}: JobPositionDetailProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const businessLocations = useBusinessLocations();

  const [phraseLanguage, setPhraseLanguage] = useState<string>('fr');
  const [selectedJobType, setSelectedJobType] = useState<JobTypes>(JobTypes.FULL_TIME);

  const enableNewUiForCreatePosition: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_NEW_UI_FOR_CREATE_POSITION];
  });

  const isLETemplateEnabled = useStoreState((s) => s.app.accounts?.configuration?.languageEvaluationTemplateEnabled);
  const accountId = useStoreState((state) => state.app.accounts?.id);
  const isScheduleMatchEnabled = useStoreState((s) => s.app.accounts?.configuration?.scheduleMatchEnabled);
  const isVirtualRecruiterEnabled = useStoreState((s) => s.app.accounts?.configuration?.virtualRecruiterEnabled);
  const isLanguageEvaluationEnabled = useStoreState((s) => s.app.accounts?.configuration?.languageEvaluationEnabled);
  const isResumeEnabled = useStoreState((s) => s.app.accounts?.configuration?.resumeEnabled);
  // const dialogFlowAgents = useStoreState((s) => s.app.accounts?.dialogFlowAgents);
  // const accountDialogFlowEnabled = useStoreState((s) => s.app.accounts?.configuration?.dialogFlowEnabled);
  const languageEvaluationPhrases = useLanguagePhrasesByAccountId(accountId ?? '');
  const workbrightEnabled = useStoreState((state) => state.app.accounts?.configuration?.workbright?.enabled);
  const fitbandEnabledInAccount = useStoreState((state) => state.app.accounts?.configuration?.fitbandTemplateEnabled);
  const isRequisitionEnabled = useStoreState((state) => state.app.accounts?.configuration?.requisitionEnabled);

  const formattedJobDescription = useMemo(() => convertLegacyTextToHtml(jobPositionData.jobDescription), [
    jobPositionData.jobDescription,
  ]);

  // filter occupations and jobs based on selected industry
  const filterOccupations = (industryId: string) =>
    occupationList
      // eslint-disable-next-line radix
      .filter((j) => j.industryRef === parseInt(industryId))
      .sort((o1, o2) => (/* istanbul ignore next */ o1.name.en.toUpperCase() < o2.name.en.toUpperCase() ? -1 : 1));

  const filterJobs = (industryId: string, occupationId: string) =>
    jobList
      // eslint-disable-next-line radix
      .filter((j) => j.occupation === parseInt(occupationId))
      .sort((j1, j2) => (/* istanbul ignore next */ j1.jobTitle.en.toUpperCase() < j2.jobTitle.en.toUpperCase() ? -1 : 1));

  /* istanbul ignore next */
  const handleJobLocationChange = (businessLocation: BusinessSelectType | null) => {
    /* istanbul ignore next */
    if (!_isNil(businessLocation) && !_isNil(setJobPositionData)) {
      setJobPositionData((prevData) => ({
        ...prevData,
        business: businessLocation.id,
        businessName: businessLocation.value,
        businessAddress: businessLocation.label,
      }));
    }
    /* istanbul ignore next */
    setFormErrors?.(clearErrorMessage(formErrors, 'business'));
  };

  useEffect(() => {
    if (jobPositionData.languageEvaluationTemplates && jobPositionData.languageEvaluationTemplates.length > 0) {
      setPhraseLanguage(jobPositionData.languageEvaluationTemplates[0].language);
    }
  }, [jobPositionData.languageEvaluationTemplates]);

  useEffect(() => {
    if (jobPositionData?.fullTime === false) {
      setSelectedJobType(JobTypes.PART_TIME);
    } else {
      setSelectedJobType(JobTypes.FULL_TIME);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPositionData.fullTime]);

  const jobTypes = Object.entries(JobTypes);

  const business: Business | undefined = useDocumentSnapshot<Business>(Collections.BUSINESSES, jobPositionData?.business);
  const isCountryAustralia = business?.country === 'Australia';

  const getJobTypeText = (value: JobTypes): string => {
    switch (value) {
      case JobTypes.FULL_TIME:
        return t('createJobPosition:jobTypedesc.fullTime');
      case JobTypes.PART_TIME:
        return t('createJobPosition:jobTypedesc.partTime');
      default:
        return '';
    }
  };

  const handleJobDescriptionChange = useCallback(
    (value: LanguageMap<string> | undefined) => {
      const content = value?.en || '';

      if (content !== jobPositionData.jobDescription) {
        handleChange(content, 'jobDescription', false);
      }
    },
    [jobPositionData.jobDescription, handleChange],
  );

  const showFeatureEverywhereExceptOnSmbCopy = !isSmbRecruiter || (isSmbRecruiter && !isCopyPostion);

  const renderNumberOfOpenings = () => {
    return (
      <Flex w={isSmbRecruiter ? '50%' : '25%'} pr={isSmbRecruiter ? '0' : '6'}>
        <Box w="100%">
          <FormControl isInvalid={hasErrorMessage(formErrors, 'noOfOpenings')} isRequired={!isRequisitionEnabled}>
            <FormLabel htmlFor="numberOfOpening">{t('createJobPosition:noOfOpenings')}</FormLabel>
            <NumberInput
              variant="filled"
              id="numberOfOpening"
              min={0}
              max={999}
              value={!_isNil(jobPositionData.noOfOpenings) ? jobPositionData.noOfOpenings : ''}
              onChange={(value) => handleChange(value, 'noOfOpenings', true)}
              data-testid="NumberOfOpeningField"
            >
              <NumberInputField />
              <NumberInputStepper borderColor="#dee1e3">
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <FormErrorMessage>{getErrorMessage(formErrors, 'noOfOpenings')}</FormErrorMessage>
          </FormControl>
        </Box>
      </Flex>
    );
  };

  // const handleAdditionalNote = (note: string) => {
  //   // setAdditionalNote(note);
  //   if (!_isNil(setJobPositionData)) {
  //     setJobPositionData((prev) => ({
  //       ...prev,
  //       offerConditions: {
  //         ...prev.offerConditions,
  //         en: note,
  //       },
  //     }));
  //   }
  // };

  const showLETemplate =
    (!positionDataToEdit && isLETemplateEnabled) || // Case 1
    (positionDataToEdit &&
      isLETemplateEnabled &&
      (jobPositionData?.leTemplateId || // ✅ If a template ID is selected
        (_isNil(jobPositionData?.leTemplateId) && _isEmpty(jobPositionData?.languageEvaluationTemplates)))) || // ✅ fallback logic
    LETemplateUsed;

  // Salary display options
  const salaryDisplayOptions = [
    {
      value: SalaryDisplayOption.STARTING_AT,
      label: t('createJobPosition:salaryDisplayOptions.startingAt'),
    },
    { value: SalaryDisplayOption.RANGE, label: t('createJobPosition:salaryDisplayOptions.range') },
    { value: SalaryDisplayOption.UP_TO, label: t('createJobPosition:salaryDisplayOptions.upTo') },
    { value: SalaryDisplayOption.FIXED, label: t('createJobPosition:salaryDisplayOptions.fixed') },
    { value: SalaryDisplayOption.HIDDEN, label: t('createJobPosition:salaryDisplayOptions.hide') },
  ];

  useEffect(() => {
    // Whenever schedule matching is enabled in position, fit band template should be mandatorily used.
    if (jobPositionData?.enableScheduleMatch && !jobPositionData?.fitBandTemplateEnable && fitbandEnabledInAccount) {
      handleChange(true, 'fitBandTemplateEnable', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPositionData?.enableScheduleMatch, jobPositionData?.fitBandTemplateEnable, handleChange, fitbandEnabledInAccount]);

  return (
    <Box backgroundColor={colors.white} borderRadius={5}>
      <Box
        p={/* istanbul ignore next */ enableNewUiForCreatePosition ? 0 : 6}
        paddingTop={/* istanbul ignore next */ enableNewUiForCreatePosition ? 6 : '1.5rem'}
      >
        <Box>
          {isCopyPostion && (
            <Flex w="100%" mt={6} mb={3}>
              <FormControl isInvalid={hasErrorMessage(formErrors, 'business')} isRequired>
                <FormLabel htmlFor="jobLocation" fontWeight="bold">
                  {t('createJobPosition:businessLocation')}
                </FormLabel>
                <Box css={ReactCustomSelectStyles} data-testid="JobLocationChangeBtn">
                  <MultiSelectDropdown
                    placeholder={t('createJobPosition:businessLocationPlaceholder')}
                    id="jobLocation"
                    handleChange={handleJobLocationChange}
                    options={businessLocations}
                    value={
                      /* istanbul ignore next */ jobPositionData.businessAddress
                        ? {
                            id: jobPositionData.business,
                            label: jobPositionData.businessName,
                            value: jobPositionData.businessAddress,
                          }
                        : null
                    }
                    data-testid="JobLocationField"
                  />
                </Box>
                <FormErrorMessage>{getErrorMessage(formErrors, 'business')}</FormErrorMessage>
              </FormControl>
            </Flex>
          )}
          <Box w="30%" mb={6}>
            <Box>
              <FormControl isInvalid={hasErrorMessage(formErrors, 'positionId')} isRequired>
                <FormLabel htmlFor="positionId">{t('createJobPosition:positionId')}</FormLabel>
                <Input
                  variant="filled"
                  id="positionId"
                  type="text"
                  value={jobPositionData.positionId}
                  onChange={(e) => handleChange(e.target.value, 'positionId', false)}
                  data-testid="PositionIdField"
                  placeholder={t('createJobPosition:positionIdPlaceholder')}
                />
                <FormErrorMessage>{getErrorMessage(formErrors, 'positionId')}</FormErrorMessage>
              </FormControl>
            </Box>
          </Box>

          {showFeatureEverywhereExceptOnSmbCopy && (
            <FormControl isInvalid={hasErrorMessage(formErrors, 'industry')} mb={8} isRequired>
              <FormLabel>{t('createJobPosition:industry')}</FormLabel>
              {industries.length <= 0 && (
                <Center p={4}>
                  <Spinner color="blue.500" size="sm" />
                </Center>
              )}
              <RadioGroup
                name="industry"
                id="industryLabel"
                css={IndustryRadiosStyle}
                colorScheme="blue"
                data-testid="industryRadio"
                value={jobPositionData.industry}
              >
                <Flex direction="row" wrap="wrap" justify="flex-start">
                  {industries.map((i) => (
                    <Radio
                      onChange={(e) => handleChange(e.target.value, 'industry', false)}
                      mr={2}
                      mt={{ base: 2, sm: 2, md: 2, lg: 2, xl: 0 }}
                      key={i.id}
                      value={String(i.id)}
                      id={String(i.id)}
                      data-testid={`radio-${String(i.id)}`}
                    >
                      <Box>
                        <img src={loadIcon(i.name.en)} alt={getTranslatedValue(i.name, i18n.language)} />
                      </Box>
                      {getTranslatedValue(i.name, i18n.language)}
                    </Radio>
                  ))}
                </Flex>
              </RadioGroup>
              <FormErrorMessage>{getErrorMessage(formErrors, 'industry')}</FormErrorMessage>
            </FormControl>
          )}

          <Flex w="100%" mb={6} flexDirection={isSmbRecruiter ? 'column' : 'row'}>
            {showFeatureEverywhereExceptOnSmbCopy && (
              <Flex w={isSmbRecruiter ? '100%' : '66%'}>
                <Box w="50%" pr={6} mb={isSmbRecruiter ? 6 : 0}>
                  <FormControl isInvalid={hasErrorMessage(formErrors, 'occupation')} isRequired>
                    <FormLabel>{t('createJobPosition:occupation')}</FormLabel>
                    <Select
                      className="filled"
                      name="occupation"
                      id="occupationLabel"
                      value={jobPositionData.occupation}
                      disabled={_isEmpty(jobPositionData.industry)}
                      data-testid="occupationSelect"
                      onChange={(e) => handleChange(e.target.value, 'occupation', false)}
                    >
                      <option value="">{t('createJobPosition:selectOccupationPlaceholder')}</option>
                      {filterOccupations(jobPositionData.industry).map((o) => (
                        <option key={o.id} value={o.id} data-testid={`occupation-${o.id}`}>
                          {getTranslatedValue(o.name, i18n.language)}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{getErrorMessage(formErrors, 'occupation')}</FormErrorMessage>
                  </FormControl>
                </Box>
                <Box w="50%" pr={6}>
                  <FormControl isInvalid={hasErrorMessage(formErrors, 'job')} isRequired>
                    <FormLabel>{t('createJobPosition:jobType')}</FormLabel>
                    <Select
                      className="filled"
                      name="job"
                      id="jobLabel"
                      value={jobPositionData.job}
                      disabled={_isEmpty(jobPositionData.occupation)}
                      data-testid="jobSelect"
                      onChange={(e) => handleJobChange(e.target.value, 'job')}
                    >
                      <option value="">{t('createJobPosition:selectJobTypePlaceholder')}</option>
                      {filterJobs(jobPositionData.industry, jobPositionData.occupation).map((j) => (
                        <option key={j.id} value={j.id} data-testid={`job-${j.id}`}>
                          {getTranslatedValue(j.jobTitle, i18n.language)}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{getErrorMessage(formErrors, 'job')}</FormErrorMessage>
                  </FormControl>
                </Box>
                {isCountryAustralia && (
                  <Box w="60%" pr={6}>
                    <FormControl isInvalid={hasErrorMessage(formErrors, 'positionType')} isRequired>
                      <FormLabel>{t('createJobPosition:employmentType')}</FormLabel>
                      <Select
                        className="filled"
                        name="positionType"
                        id="positionTypeLabel"
                        value={jobPositionData.positionType ?? ''}
                        disabled={_isEmpty(jobPositionData.job)}
                        data-testid="positionTypeSelect"
                        onChange={(e) => {
                          const val = e.target.value;
                          handleChange(val, 'positionType', false);
                          const isFullTime = val !== PositionType.PART_TIME && val !== '';
                          handleChange(isFullTime, 'fullTime', false);
                        }}
                      >
                        <option value="">{t('createJobPosition:selectEmploymentTypePlaceholder')}</option>
                        {Object.entries({
                          [PositionType.FULL_TIME]: t(`jobPosition:full_time`),
                          [PositionType.PART_TIME]: t(`jobPosition:part_time`),
                          [PositionType.CASUAL]: t(`jobPosition:casual`),
                          [PositionType.SEASONAL]: t(`jobPosition:seasonal`),
                          [PositionType.CONTRACT]: t(`jobPosition:contract`),
                        }).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage>{getErrorMessage(formErrors, 'positionType')}</FormErrorMessage>
                    </FormControl>
                  </Box>
                )}
              </Flex>
            )}
            <Flex justifyContent="space-between" w={isSmbRecruiter ? '100%' : '33%'}>
              <Box w={isSmbRecruiter ? '50%' : '100%'} pr={isSmbRecruiter ? '6' : 'unset'}>
                <FormControl isInvalid={hasErrorMessage(formErrors, 'jobCustomName')} isRequired>
                  <FormLabel htmlFor="jobTitle">{t('createJobPosition:jobTitle')}</FormLabel>
                  <Input
                    variant="filled"
                    id="jobTitle"
                    type="text"
                    value={jobPositionData.jobCustomName}
                    onChange={(e) => handleChange(e.target.value, 'jobCustomName', false)}
                    data-testid="JobCustomNameField"
                  />
                  <FormErrorMessage>{getErrorMessage(formErrors, 'jobCustomName')}</FormErrorMessage>
                </FormControl>
              </Box>

              {isSmbRecruiter && renderNumberOfOpenings()}
            </Flex>
          </Flex>
          <Flex mb={6}>
            {!isSmbRecruiter && renderNumberOfOpenings()}

            {isSmbRecruiter && (
              <Box w="50%" pr={6}>
                <FormControl isRequired>
                  <FormLabel htmlFor="employmentType">{t('createJobPosition:smbEmploymentType')}</FormLabel>
                  <RadioGroup value={selectedJobType} height={8} display="flex" alignItems="flex-end">
                    <Stack spacing={4} direction="row">
                      {jobTypes.map(([key, eachJobType]) => {
                        return (
                          <Radio
                            key={key}
                            value={eachJobType}
                            data-testid={`jobType-${eachJobType}`}
                            onChange={(e) => {
                              if (e.target.value === JobTypes.FULL_TIME) {
                                handleChange(true, 'fullTime', false);
                              } else {
                                handleChange(false, 'fullTime', false);
                              }
                              setSelectedJobType(e.target.value as JobTypes);
                            }}
                          >
                            {getJobTypeText(eachJobType)}
                          </Radio>
                        );
                      })}
                    </Stack>
                  </RadioGroup>
                </FormControl>
              </Box>
            )}

            <Flex w={isSmbRecruiter ? '50%' : '100%'} justifyItems="space-between">
              <Box w="20%" mr={4}>
                <FormControl isRequired>
                  <FormLabel htmlFor="salary">{t('createJobPosition:salary')}</FormLabel>
                  <Select
                    className="filled"
                    id="salary"
                    value={jobPositionData.proposedSalary.unit}
                    onChange={(e) => handleChange(e.target.value, 'proposedSalary.unit', false)}
                    data-testid="SalaryUnitField"
                  >
                    <option value="annual">{t('createJobPosition:proposedSalaryUnitOptions.annual')}</option>
                    <option value="hourly">{t('createJobPosition:proposedSalaryUnitOptions.hourly')}</option>
                    <option value="custom">{t('createJobPosition:proposedSalaryUnitOptions.custom')}</option>
                  </Select>
                </FormControl>
              </Box>
              <Box w="20%" mr={4}>
                <FormControl isInvalid={hasErrorMessage(formErrors, 'proposedSalary.minValue')}>
                  <FormLabel>&nbsp;</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>$</InputLeftAddon>
                    <Input
                      type="text"
                      variant="filled"
                      placeholder={jobPositionData.proposedSalary.unit === 'annual' ? '2000' : '20'}
                      value={
                        /* istanbul ignore next */
                        !_isNil(jobPositionData.proposedSalary.minValue)
                          ? getTranslatedStringValue(jobPositionData.proposedSalary.minValue, i18n.language)
                          : ''
                      }
                      onChange={(e) => handleSalaryChange(e.target.value, 'minValue')}
                      data-testid="MinValueField"
                    />
                  </InputGroup>
                  <FormErrorMessage>{getErrorMessage(formErrors, 'proposedSalary.minValue')}</FormErrorMessage>
                </FormControl>
              </Box>
              <>
                <Box mr={4}>
                  <FormControl>
                    <FormLabel>&nbsp;</FormLabel>
                    <Text mt={5}>{t('createJobPosition:to')}</Text>
                  </FormControl>
                </Box>

                <Box w="20%" mr={4}>
                  <FormControl isInvalid={hasErrorMessage(formErrors, 'proposedSalary.maxValue')}>
                    <FormLabel>&nbsp;</FormLabel>
                    <InputGroup>
                      <InputLeftAddon>$</InputLeftAddon>
                      <Input
                        type="text"
                        variant="filled"
                        placeholder={jobPositionData.proposedSalary.unit === 'annual' ? '5000' : '50'}
                        value={
                          /* istanbul ignore next */
                          !_isNil(jobPositionData.proposedSalary.maxValue)
                            ? getTranslatedStringValue(jobPositionData.proposedSalary.maxValue, i18n.language)
                            : ''
                        }
                        onChange={(e) => handleSalaryChange(e.target.value, 'maxValue')}
                        data-testid="MaxValueField"
                      />
                    </InputGroup>
                    <FormErrorMessage>{getErrorMessage(formErrors, 'proposedSalary.maxValue')}</FormErrorMessage>
                  </FormControl>
                </Box>
              </>
              <Box w="15%">
                {jobPositionData.proposedSalary.unit === 'custom' && (
                  <FormControl isInvalid={hasErrorMessage(formErrors, 'proposedSalary.customUnit')}>
                    <FormLabel htmlFor="per">{t('createJobPosition:per')}</FormLabel>
                    <Input
                      variant="filled"
                      id="per"
                      type="text"
                      placeholder={t('createJobPosition:unit')}
                      value={
                        !_isNil(jobPositionData.proposedSalary.customUnit) ? jobPositionData.proposedSalary.customUnit : undefined
                      }
                      onChange={(e) => handleChange(e.target.value, 'proposedSalary.customUnit', false)}
                      data-testid="CustomUnitTypeField"
                    />
                    <FormErrorMessage>{getErrorMessage(formErrors, 'proposedSalary.customUnit')}</FormErrorMessage>
                  </FormControl>
                )}
              </Box>
              <Box w="30%" ml={4}>
                <FormControl isInvalid={hasErrorMessage(formErrors, 'salaryDisplayOption')}>
                  <FormLabel>
                    {t('createJobPosition:salaryDisplayOptions.label')}
                    <Tooltip label={t('createJobPosition:salaryDisplayOptions:tooltipLabel')} placement="top">
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <AiOutlineInfoCircle
                          size={20}
                          style={{ marginLeft: '3px', paddingTop: '5px', color: colors.gray[500] }}
                          cursor="pointer"
                        />
                      </span>
                    </Tooltip>
                  </FormLabel>
                  <Box w="100%">
                    <Select
                      value={jobPositionData?.salaryDisplayOption}
                      onChange={(e) => {
                        handleChange(e.target.value, 'salaryDisplayOption', false);
                      }}
                      data-testid="SalaryDisplayOptionField"
                    >
                      {salaryDisplayOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </Box>

                  <FormErrorMessage>{getErrorMessage(formErrors, 'salaryDisplayOption')}</FormErrorMessage>
                </FormControl>
              </Box>
            </Flex>
          </Flex>
          <Flex mb={6}>
            {!isSmbRecruiter && (
              <Box w="100%">
                <Flex>
                  <Box w="40%" mr={4}>
                    <FormControl>
                      <FormLabel htmlFor="experience">{t('createJobPosition:experience')}</FormLabel>
                      <Select
                        className="filled"
                        id="experience"
                        value={!_isNil(jobPositionData.experienceFilterType) ? jobPositionData.experienceFilterType : undefined}
                        onChange={(e) => handleChange(e.target.value, 'experienceFilterType', false)}
                        data-testid="ExperienceFilterTypeField"
                      >
                        <option value="" disabled selected>
                          {t('createJobPosition:selectFilterByType')}
                        </option>
                        <option value="preferred">{t('createJobPosition:experienceFilterTypeOptions.preferred')}</option>
                        <option value="maximum">{t('createJobPosition:experienceFilterTypeOptions.maximum')}</option>
                        <option value="minimum">{t('createJobPosition:experienceFilterTypeOptions.minimum')}</option>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box w="60%">
                    <FormControl>
                      <FormLabel>&nbsp;</FormLabel>
                      <Flex alignItems="center">
                        <NumberInput
                          min={0}
                          max={100}
                          variant="filled"
                          id="experienceYears"
                          type="number"
                          w="40%"
                          value={jobPositionData.requiredExperience ? jobPositionData.requiredExperience : ''}
                          onChange={(value) => handleChange(value, 'requiredExperience', true)}
                          data-testid="RequiredExperienceField"
                        >
                          <NumberInputField />
                          <NumberInputStepper borderColor="#dee1e3">
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                        <Text ml={4} w="60%">
                          {t('createJobPosition:yearsOfExperience')}
                        </Text>
                      </Flex>
                    </FormControl>
                  </Box>
                </Flex>
              </Box>
            )}
          </Flex>

          {showFeatureEverywhereExceptOnSmbCopy && (
            <Box w="100%" mb={6}>
              <Box>
                <FormControl isInvalid={hasErrorMessage(formErrors, 'jobDescription')} isRequired={isSmbRecruiter}>
                  <FormLabel htmlFor="jobDescription">{t('createJobPosition:jobDescription')}</FormLabel>
                  <AdditionalNoteEditor
                    data-testid="JobDescriptionField"
                    additionalNote={{ en: formattedJobDescription || '' }}
                    handleNote={handleJobDescriptionChange}
                    showHeader={false}
                    showToolbar
                    disableLanguageSupport
                  />
                  <FormErrorMessage>{getErrorMessage(formErrors, 'jobDescription')}</FormErrorMessage>
                </FormControl>
              </Box>
            </Box>
          )}
          <Divider mt={6} mb={6} />

          {!isSmbRecruiter && (
            <Fragment>
              <Flex width="100%" justifyContent="flex-start" alignItems="flex-start" mb={8} mt={4}>
                {isScheduleMatchEnabled && (
                  <FormControl
                    isInvalid={hasErrorMessage(formErrors, 'enableScheduleMatch')}
                    mr={20}
                    data-testid="enableScheduleMatch"
                    width="unset"
                    maxWidth="33%"
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                  >
                    <Flex justifyContent="center" alignItems="center">
                      <Text mr={2}>{t('createJobPosition:scheduleMatch')}</Text>
                      <Switch
                        size="sm"
                        isChecked={jobPositionData?.enableScheduleMatch}
                        onChange={(e) => handleChange(e.target.checked, 'enableScheduleMatch', false)}
                        data-testid="enableScheduleMatch"
                      />
                      <Tooltip hasArrow placement="right" label={t('createJobPosition:scheduleMatchTooltip')}>
                        <Box ml={2}>
                          <MdOutlineInfo />
                        </Box>
                      </Tooltip>
                    </Flex>
                    <FormErrorMessage>{getErrorMessage(formErrors, 'enableScheduleMatch')}</FormErrorMessage>
                  </FormControl>
                )}
                {isVirtualRecruiterEnabled && (
                  <Flex justifyContent="center" alignItems="center" mr={20}>
                    <Text mr={2}>{t('createJobPosition:virtualRecruiter')}</Text>
                    <Switch
                      size="sm"
                      isChecked={jobPositionData?.virtualRecruiterEnabled}
                      onChange={(e) => handleChange(e.target.checked, 'virtualRecruiterEnabled', false)}
                      data-testid="virtualRecruiterEnabled"
                    />
                  </Flex>
                )}
                {isResumeEnabled && (
                  <Flex justifyContent="center" alignItems="center" mr={20}>
                    <Text mr={2}>{t('createJobPosition:resume')}</Text>
                    <Switch
                      size="sm"
                      isChecked={jobPositionData?.resumeEnabled}
                      onChange={(e) => handleChange(e.target.checked, 'resumeEnabled', false)}
                      data-testid="resumeEnabled"
                    />
                  </Flex>
                )}

                {workbrightEnabled && (
                  <Flex justifyContent="center" alignItems="center" mr={20}>
                    <Text mr={2}>{t('messaging:actions.i9Check.label')}</Text>
                    <Switch
                      size="sm"
                      isChecked={jobPositionData?.i9Enabled}
                      onChange={(e) => handleChange(e.target.checked, 'i9Enabled', false)}
                      data-testid="i9RequestEnabled"
                    />
                  </Flex>
                )}
              </Flex>
            </Fragment>
          )}

          {!isSmbRecruiter && (
            <ModernHireSelect
              modernHireEnabled={jobPositionData?.modernHireEnable || false}
              modernHireThreshold={jobPositionData?.modernHirePassThreshold || 0}
              modernHireTemplateId={jobPositionData?.modernHireTemplateId || ''}
              onHandleChange={handleChange}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
            />
          )}

          {!isSmbRecruiter && (
            <DialogFlowSelect
              dialogFlowEnabled={jobPositionData?.dialogFlowEnabled || false}
              dialogFlowAgent={jobPositionData?.dialogFlowAgents || []}
              onHandleDialogFlowCheckbox={handleDialogFlowCheckbox}
              onHandleAgents={handleDialogFlowAgents}
              formErrors={formErrors}
            />
          )}

          <HireFormTemplateSelect
            enableHireFormSolution={jobPositionData?.enableHireFormSolution || false}
            hireFormTemplateId={jobPositionData?.hireFormTemplateId || null}
            onHireFormSolutionChange={handleChange}
            onHireFormTemplateChange={handleChange}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
          />

          {!isSmbRecruiter && (
            <FitbandEvaluationSelect
              fitbandTemplateEnabled={jobPositionData?.fitBandTemplateEnable || false}
              fitBandTemplateId={jobPositionData?.fitBandTemplateId || ''}
              onHandleChange={handleChange}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              isDisabled={Boolean(jobPositionData?.enableScheduleMatch)}
            />
          )}

          {isLanguageEvaluationEnabled && !isSmbRecruiter && (
            <>
              {showLETemplate ? (
                <>
                  <LanguageEvaluationTemplateSelect
                    leTemplateEnable={jobPositionData?.languageEvaluationEnable || false}
                    leTemplateId={jobPositionData?.leTemplateId || ''}
                    languageEvaluationThreshold={jobPositionData?.languageEvaluationPassThreshold || 0}
                    onHandleChange={handleChange}
                    formErrors={formErrors}
                    setFormErrors={setFormErrors}
                    audioEvaluationOptions={audioEvaluationOptions}
                  />
                  {positionDataToEdit &&
                    jobPositionData?.languageEvaluationTemplates &&
                    jobPositionData?.languageEvaluationTemplates?.length > 0 &&
                    _isNil(jobPositionData?.leTemplateId) &&
                    jobPositionData?.languageEvaluationEnable && (
                      <Checkbox
                        mb={2}
                        isChecked={LETemplateUsed}
                        fontSize="sm"
                        onChange={(e) => setLETemplateUsed(e.target.checked)}
                        data-testid="useLanguageEvaluationTemplate"
                      >
                        {t('createJobPosition:useLETemplate')}
                      </Checkbox>
                    )}
                </>
              ) : (
                <Box mb={6} pb={4} pt={3}>
                  <Checkbox
                    mb={3}
                    isChecked={!_isNil(jobPositionData.languageEvaluationTemplates)}
                    onChange={(e) => handleLanguageEvaluation(e.target.checked)}
                    data-testid="enableLanguageEvaluationField"
                  >
                    {t('createJobPosition:languageEvaluation')}
                  </Checkbox>
                  {isLETemplateEnabled && !_isNil(jobPositionData.languageEvaluationTemplates) && (
                    <Checkbox
                      mb={3}
                      ml={3}
                      isChecked={LETemplateUsed}
                      fontSize="sm"
                      onChange={(e) => setLETemplateUsed(e.target.checked)}
                      data-testid="useLanguageEvaluationTemplate"
                    >
                      {t('createJobPosition:useLETemplate')}
                    </Checkbox>
                  )}
                  {!_isNil(jobPositionData.languageEvaluationTemplates) && (
                    <Box mt={1}>
                      <Flex mb={2} alignItems="flex-start">
                        <FormControl d="flex" alignItems="center" justifyContent="normal" w="48%" mr="4%">
                          <FormLabel mb={0} htmlFor="phrase-language">
                            {t('createJobPosition:selectLanguage')}
                          </FormLabel>
                          <Select
                            value={phraseLanguage}
                            w="60%"
                            data-testid="phraseLanguageChange"
                            id="phrase-language"
                            onChange={(e) => {
                              setPhraseLanguage(e.target.value);
                              handleLanguageChange();
                            }}
                          >
                            <option value="en">{t('createJobPosition:english')}</option>
                            <option value="fr">{t('createJobPosition:french')}</option>
                          </Select>
                        </FormControl>
                        {languageEvaluationPhrases && languageEvaluationPhrases.length > 0 ? (
                          <FormControl
                            d="flex"
                            flexDirection="column"
                            alignItems="baseline"
                            justifyContent="space-between"
                            w="48%"
                            marginRight="4%"
                            isRequired
                            isInvalid={hasErrorMessage(formErrors, 'languageEvaluationTemplates')}
                          >
                            <Flex alignItems="center">
                              <FormLabel mb={0} htmlFor="phrase">
                                {t('createJobPosition:phraseId')}
                              </FormLabel>

                              <Menu closeOnSelect={false}>
                                <MenuButton
                                  variant="outline"
                                  as={Button as any}
                                  colorScheme="gray"
                                  pr={2}
                                  borderColor="gray.150"
                                  backgroundColor="gray.150"
                                  boxShadow="0 0 0 1px #F5F8FA"
                                  height="3rem"
                                  padding="0.4rem 1rem"
                                  display="flex"
                                  fontWeight="normal"
                                  justifyContent="space-between"
                                  textAlign="left"
                                  rightIcon={<ChevronDownIcon style={{ height: '20px', width: '20px' }} />}
                                >
                                  {t('createJobPosition:selectPhrase')}
                                </MenuButton>
                                <MenuList minWidth="240px" borderColor="gray.50">
                                  <Box>
                                    <Stack p={2} spacing={3} direction="column">
                                      {languageEvaluationPhrases
                                        .filter(
                                          (langEvalPhrase: LanguageEvaluationPhrases) =>
                                            langEvalPhrase.language === phraseLanguage,
                                        )
                                        .map((phrase: LanguageEvaluationPhrases) => (
                                          <Checkbox
                                            defaultIsChecked={_some(jobPositionData.languageEvaluationTemplates, {
                                              phraseId: phrase.id,
                                            })}
                                            colorScheme="blue"
                                            px={3}
                                            key={phrase.id}
                                            value={phrase.id}
                                            data-testid={`phraseCheckBox-${phrase.id}`}
                                            onChange={(e) =>
                                              handleLanguageEvaluationPhrase(e.target.checked, phraseLanguage, phrase)
                                            }
                                          >
                                            {phrase.phraseId}
                                          </Checkbox>
                                        ))}
                                    </Stack>
                                  </Box>
                                </MenuList>
                              </Menu>
                            </Flex>
                            <FormErrorMessage>{getErrorMessage(formErrors, 'languageEvaluationTemplates')}</FormErrorMessage>
                          </FormControl>
                        ) : (
                          /* istanbul ignore next */
                          <Flex alignItems="center">
                            <AiOutlineInfoCircle style={{ width: '36px' }} />
                            <Text ml="6px" fontStyle="italic">
                              {t('createJobPosition:noPhrasetext')}
                            </Text>
                          </Flex>
                        )}

                        <FormControl
                          d="flex"
                          alignItems="center"
                          flexDirection="column"
                          justifyContent="normal"
                          w="48%"
                          mr="4%"
                          isRequired
                          isInvalid={hasErrorMessage(formErrors, 'languageEvaluationPassThreshold')}
                        >
                          <Flex>
                            <FormLabel mb={0} htmlFor="languageEvaluationPassThreshold-label">
                              {t('createJobPosition:selectLanguageEvaluationPassThreshold')}
                            </FormLabel>
                            <Select
                              value={jobPositionData?.languageEvaluationPassThreshold}
                              isDisabled={!jobPositionData.languageEvaluationTemplates.length}
                              w="60%"
                              name="languageEvaluationPassThreshold"
                              id="languageEvaluationPassThreshold-label"
                              data-testid="languageThresholdChange"
                              onChange={(e) => {
                                handleChange(Number(e.target.value), 'languageEvaluationPassThreshold', false);
                              }}
                            >
                              <option value="" selected disabled>
                                {t('createJobPosition:selectPassThreshold')}
                              </option>
                              {audioEvaluationOptions
                                ?.filter((option) => option.id !== LanguageEvaluationRating.NEEDS_REVIEW)
                                .map((option) => (
                                  <option key={option.order} value={option.order}>
                                    {getTranslatedValue(option.label, i18n.language)}
                                  </option>
                                ))}
                            </Select>
                          </Flex>
                          <FormErrorMessage>{getErrorMessage(formErrors, 'languageEvaluationPassThreshold')}</FormErrorMessage>
                        </FormControl>
                      </Flex>
                      {languageEvaluationPhrases &&
                        languageEvaluationPhrases.length > 0 &&
                        jobPositionData.languageEvaluationTemplates?.map((template) => (
                          <Box key={template.phraseId} mb={3}>
                            <FormLabel htmlFor="phrase" fontWeight="bold" fontSize="14px">
                              {
                                languageEvaluationPhrases.filter(
                                  (langPhrase: LanguageEvaluationPhrases) => langPhrase.id === template.phraseId,
                                )[0]?.phraseId
                              }
                            </FormLabel>
                            <Box fontSize="14px" border="1px solid" borderColor="gray.50" borderRadius={3} p={3}>
                              {template.phrase}
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}

          <Divider mt={6} mb={6} />

          {/* Todo: will be uncomment in next release */}
          {/* <Box w="100%"> */}
          {/*  <Box> */}
          {/*    <FormControl> */}
          {/*      <FormLabel htmlFor="jobDescription" fontWeight="bold"> */}
          {/*        {t('createJobPosition:recruiterNotes')} */}
          {/*      </FormLabel> */}
          {/*      <Textarea */}
          {/*        variant="filled" */}
          {/*        id="recruiterNote" */}
          {/*        placeholder={t('createJobPosition:recruiterNotePlaceholder')} */}
          {/*        value={ */}
          {
            /*          /* istanbul ignore next */
            //! _isNil(jobPositionData.recruiterNote) ? jobPositionData.recruiterNote : undefined} */
          }
          {/*        } */}
          {/*        onChange={(e) => handleChange(e.target.value, 'recruiterNote', false)} */}
          {/*        data-testid="recruiterNote" */}
          {/*      /> */}
          {/*    </FormControl> */}
          {/*  </Box> */}
          {/* </Box> */}
        </Box>
      </Box>
    </Box>
  );
};
